export interface ImageCrop {
  x: number
  y: number
  w: number
  h: number
}

interface ShapeStyle {
  id: string
  color: string
  strokeWidth: number
}

export interface BoxShape extends ShapeStyle {
  type: 'rectangle' | 'ellipse'
  x: number
  y: number
  width: number
  height: number
  rotation: number
}

export interface LineShape extends ShapeStyle {
  type: 'line' | 'arrow'
  points: [number, number, number, number]
}

export interface TextShape extends ShapeStyle {
  type: 'text'
  x: number
  y: number
  text: string
  fontSize: number
  rotation: number
}

export type AnnotationShape = BoxShape | LineShape | TextShape

export interface Annotations {
  width: number
  height: number
  shapes: AnnotationShape[]
}

export interface ImageEdit {
  version: 4
  crop: ImageCrop | null
  annotations: Annotations | null
}

export interface DraftImageEdit {
  uploadId: string
  editLayer: string
}

export type CropHandle = 'move' | 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'

export const FULL_CROP: ImageCrop = { x: 0, y: 0, w: 1, h: 1 }
export const MIN_CROP_SIZE = 0.05

const EPSILON = 0.001

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

export function adjustCrop(crop: ImageCrop, handle: CropHandle, dx: number, dy: number): ImageCrop {
  if (handle === 'move') {
    return {
      ...crop,
      x: clamp(crop.x + dx, 0, 1 - crop.w),
      y: clamp(crop.y + dy, 0, 1 - crop.h),
    }
  }

  let left = crop.x
  let top = crop.y
  let right = crop.x + crop.w
  let bottom = crop.y + crop.h
  if (handle.includes('w')) left = clamp(left + dx, 0, right - MIN_CROP_SIZE)
  if (handle.includes('e')) right = clamp(right + dx, left + MIN_CROP_SIZE, 1)
  if (handle.includes('n')) top = clamp(top + dy, 0, bottom - MIN_CROP_SIZE)
  if (handle.includes('s')) bottom = clamp(bottom + dy, top + MIN_CROP_SIZE, 1)
  return { x: left, y: top, w: right - left, h: bottom - top }
}

export function isFullCrop(crop: ImageCrop | null): boolean {
  return (
    !crop ||
    (Math.abs(crop.x) < EPSILON &&
      Math.abs(crop.y) < EPSILON &&
      Math.abs(crop.w - 1) < EPSILON &&
      Math.abs(crop.h - 1) < EPSILON)
  )
}

function scaleShape(shape: AnnotationShape, sx: number, sy: number): AnnotationShape {
  const stroke = shape.strokeWidth * Math.min(sx, sy)
  switch (shape.type) {
    case 'line':
    case 'arrow': {
      const [x1, y1, x2, y2] = shape.points
      return { ...shape, strokeWidth: stroke, points: [x1 * sx, y1 * sy, x2 * sx, y2 * sy] }
    }
    case 'text':
      return {
        ...shape,
        x: shape.x * sx,
        y: shape.y * sy,
        fontSize: shape.fontSize * Math.min(sx, sy),
      }
    default:
      return {
        ...shape,
        strokeWidth: stroke,
        x: shape.x * sx,
        y: shape.y * sy,
        width: shape.width * sx,
        height: shape.height * sy,
      }
  }
}

export function scaleAnnotations(
  annotations: Annotations,
  width: number,
  height: number,
): Annotations {
  const sx = width / annotations.width
  const sy = height / annotations.height
  return { width, height, shapes: annotations.shapes.map((shape) => scaleShape(shape, sx, sy)) }
}

export function createImageEdit(
  crop: ImageCrop | null,
  annotations: Annotations | null,
): ImageEdit {
  return {
    version: 4,
    crop: isFullCrop(crop) ? null : crop,
    annotations: annotations?.shapes.length ? annotations : null,
  }
}

export function isEmptyImageEdit(edit: ImageEdit): boolean {
  return !edit.crop && !edit.annotations
}

export function parseImageEdit(json: string | null | undefined): ImageEdit | undefined {
  if (!json) return undefined
  try {
    const parsed = JSON.parse(json)
    return parsed?.version === 4 ? (parsed as ImageEdit) : undefined
  } catch {
    return undefined
  }
}
